/**
 * Canvas Toolbar Component - Floating toolbar for panel controls
 * Provides add panel dropdown, reset layout, grid toggle, and region switcher
 */

import React, { useState, useRef } from 'react';
import { RegionId, RegionHeader } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { listPanelTypes, getPanelEntry } from './panel-registry';
import { RegionSwitcherModal } from './region-switcher-modal';
import type { PanelType } from './panel-layout-store';
import type { PendingInvite, WhisperEntry } from './use-backend';
import { NotificationCenter } from './notification-center';

export interface CanvasToolbarProps {
  onAddPanel: (type: PanelType) => void;
  onResetLayout: () => void;
  onToggleGrid: () => void;
  showGrid: boolean;
  userIdentity?: string;
  currentRegionId?: RegionId | null;
  homeRegionId?: RegionId | null;
  regions?: RegionHeader[];
  onSwitchRegion?: (regionId: RegionId) => void;
  onRegionCreated?: (regionId: RegionId) => void;
  unreadCount?: number;
  onOpenSettings?: () => void;
  pendingInvites?: PendingInvite[];
  whispers?: WhisperEntry[];
  onOpenPanel?: (type: string) => void;
}

/**
 * Canvas Toolbar Component
 * Features:
 * - Region switcher button with modal for browsing/creating regions
 * - Add Panel dropdown with all panel types
 * - Reset Layout button
 * - Grid toggle
 * - User identity indicator
 */
export function CanvasToolbar({
  onAddPanel,
  onResetLayout,
  onToggleGrid,
  showGrid,
  userIdentity,
  currentRegionId,
  homeRegionId,
  regions = [],
  onSwitchRegion,
  onRegionCreated,
  unreadCount = 0,
  onOpenSettings,
  pendingInvites = [],
  whispers = [],
  onOpenPanel,
}: CanvasToolbarProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [showIdentityTooltip, setShowIdentityTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [copiedId, setCopiedId] = useState(false);
  const addPanelButtonRef = useRef<HTMLButtonElement>(null);
  const identitySpanRef = useRef<HTMLSpanElement>(null);
  const panelTypes = listPanelTypes();

  const handleAddPanelClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (addPanelButtonRef.current) {
      const rect = addPanelButtonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2, // center point of the button
      });
    }
    setShowDropdown(!showDropdown);
  };

  // Get current region name
  const currentRegion = regions.find((r) => r.id === currentRegionId);
  const currentRegionName =
    currentRegion?.name || (currentRegionId ? `#${currentRegionId.slice(0, 8)}` : 'No Region');

  return (
    <>
      {/* Toolbar - Translucent Glass */}
      <div
        data-tour="toolbar"
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 20px',
          background: CANVAS_THEME.toolbarBackground,
          backdropFilter: CANVAS_THEME.toolbarBackdrop,
          WebkitBackdropFilter: CANVAS_THEME.toolbarBackdrop,
          border: CANVAS_THEME.toolbarBorder,
          borderRadius: '28px',
          boxShadow: CANVAS_THEME.toolbarShadow,
          zIndex: CANVAS_THEME.zIndex.toolbar,
        }}
      >
        {/* Region Switcher Button */}
        {onSwitchRegion && (
          <>
            <button
              onClick={() => setShowRegionModal(true)}
              title="Switch or create regions"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: CANVAS_THEME.accentRgba(0.15),
                border: `1px solid ${CANVAS_THEME.accentRgba(0.3)}`,
                borderRadius: '8px',
                cursor: 'pointer',
                color: CANVAS_THEME.accentColor,
                fontWeight: '500',
                fontSize: '13px',
                transition: `all ${CANVAS_THEME.transitionShift}`,
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = CANVAS_THEME.accentRgba(0.25);
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = CANVAS_THEME.accentRgba(0.15);
              }}
            >
              {currentRegionName}
            </button>

            <div
              style={{ width: '1px', height: '20px', background: 'rgba(255, 255, 255, 0.18)' }}
            />
          </>
        )}

        {/* Add Panel Button */}
        <div style={{ position: 'relative' }}>
          {unreadCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: '#f04747',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
          <button
            ref={addPanelButtonRef}
            data-tour="add-panel"
            onClick={handleAddPanelClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: CANVAS_THEME.accentRgba(0.15),
              border: `1px solid ${CANVAS_THEME.accentRgba(0.3)}`,
              borderRadius: '8px',
              cursor: 'pointer',
              color: CANVAS_THEME.accentColor,
              fontWeight: '500',
              fontSize: '13px',
              transition: `all ${CANVAS_THEME.transitionShift}`,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.background = CANVAS_THEME.accentRgba(0.25);
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.background = CANVAS_THEME.accentRgba(0.15);
            }}
          >
            + Open
          </button>

          {/* Dropdown Menu - 2-Row Horizontal Layout, Glass, Always On Top */}
          {showDropdown && (
            <div
              style={{
                position: 'fixed',
                top: `${dropdownPos.top}px`,
                left: `${dropdownPos.left}px`,
                transform: 'translateX(-50%)',
                background: 'rgba(255, 255, 255, 0.65)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                borderRadius: '12px',
                boxShadow: CANVAS_THEME.shadowMedium,
                zIndex: 99999,
                overflow: 'hidden',
                pointerEvents: 'auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 100px)',
                gap: '0',
                width: '600px',
                maxWidth: 'calc(100vw - 40px)',
                maxHeight: '60vh',
                overflowY: 'auto',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {panelTypes.filter((type) => !getPanelEntry(type).hidden).map((type) => {
                const entry = getPanelEntry(type);

                return (
                  <button
                    key={type}
                    onClick={() => {
                      onAddPanel(type);
                      setShowDropdown(false);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '14px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '12px',
                      color: CANVAS_THEME.textPrimary,
                      transition: `all ${CANVAS_THEME.transitionShift}`,
                      minHeight: '70px',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.background =
                        CANVAS_THEME.accentRgba(0.08);
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: '22px', marginBottom: '4px' }}>{entry.icon}</span>
                    <div style={{ fontWeight: '500' }}>{entry.title}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Notification Center */}
        <NotificationCenter
          pendingInvites={pendingInvites}
          whispers={whispers}
          onOpenPanel={onOpenPanel ?? (() => {})}
        />

        {/* User Identity */}
        {userIdentity && (
          <>
            <div
              style={{ width: '1px', height: '20px', background: 'rgba(255, 255, 255, 0.18)' }}
            />
            <span
              ref={identitySpanRef}
              data-tour="identity"
              role="button"
              tabIndex={0}
              onClick={() => onOpenSettings?.()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onOpenSettings?.();
              }}
              onMouseEnter={() => {
                if (identitySpanRef.current) {
                  const rect = identitySpanRef.current.getBoundingClientRect();
                  setTooltipPos({
                    top: rect.bottom + 8,
                    left: rect.left + rect.width / 2,
                  });
                }
                setShowIdentityTooltip(true);
              }}
              onMouseLeave={() => setShowIdentityTooltip(false)}
              style={{
                fontSize: '12px',
                color: CANVAS_THEME.textSecondary,
                fontFamily: 'monospace',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {userIdentity}
            </span>
          </>
        )}
      </div>

      {/* Region Switcher Modal */}
      {onSwitchRegion && (
        <RegionSwitcherModal
          isOpen={showRegionModal}
          onClose={() => setShowRegionModal(false)}
          regions={regions}
          currentRegionId={currentRegionId || undefined}
          homeRegionId={homeRegionId || undefined}
          onSwitchRegion={onSwitchRegion}
          onRegionCreated={onRegionCreated}
        />
      )}

      {/* Close dropdown when clicking outside */}
      {showDropdown && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: CANVAS_THEME.zIndex.panels - 1, // Lower z-index to not block dropdown
          }}
          onClick={() => setShowDropdown(false)}
        />
      )}

      {/* Identity Tooltip */}
      {showIdentityTooltip && userIdentity && (
        <div
          onMouseEnter={() => setShowIdentityTooltip(true)}
          onMouseLeave={() => setShowIdentityTooltip(false)}
          style={{
            position: 'fixed',
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translateX(-50%)',
            background: 'rgba(255, 255, 255, 0.18)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '10px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)',
            padding: '10px 14px',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            minWidth: '180px',
            pointerEvents: 'auto',
          }}
        >
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              fontWeight: '600',
              color: CANVAS_THEME.textPrimary,
            }}
          >
            {userIdentity}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: CANVAS_THEME.textSecondary,
            }}
          >
            No account needed
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(userIdentity).then(() => {
                setCopiedId(true);
                setTimeout(() => setCopiedId(false), 1500);
              });
            }}
            style={{
              marginTop: '2px',
              padding: '4px 10px',
              background: CANVAS_THEME.accentRgba(0.15),
              border: `1px solid ${CANVAS_THEME.accentRgba(0.3)}`,
              borderRadius: '6px',
              color: CANVAS_THEME.accentColor,
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            {copiedId ? 'Copied!' : 'Copy ID'}
          </button>
        </div>
      )}
    </>
  );
}
